import { TEAMNA_PLAN } from "./pricing.ts";

/**
 * Teamna subscription billing: a business pays Teamna. Not the money a business collects from its
 * members (that is `payments`). Spec: teamna/strategy/subscription-billing-spec.md in the strategy
 * repo. Stripe holds the money; our `business_subscriptions` row is a copy, and this file decides
 * what that copy means for access. Desk API, Desk web, mobile and Control Center all read it here.
 */

export const TRIAL_DAYS = TEAMNA_PLAN.trialDays;
/** Days after the first failed charge (or a trial that ended with no card) before Desk is read-only. */
export const GRACE_DAYS = 3;
/** The trial banner shows in the last days of a trial with no card. */
export const TRIAL_BANNER_DAYS = 3;

/** Spec section 2.1. Stored in `business_subscriptions.state`. */
export const billingStates = ["trialing", "active", "canceling", "past_due", "paused", "ended"] as const;
export type BillingState = (typeof billingStates)[number];

export const pauseAccessModes = ["full", "read_only"] as const;
export type PauseAccess = (typeof pauseAccessModes)[number];

/** What `billing_events.kind` holds. */
export const billingEventKinds = [
  "subscription_created",
  "card_added",
  "payment_succeeded",
  "payment_failed",
  "state_changed",
  "read_only_started",
  "access_restored",
  "reconcile_fixed",
  /** The trial ends within a day and there is no card (an alert, once for each trial end). */
  "trial_ending",
  /** The billing email changed: by the owner or an admin in Desk, or in Stripe (portal, dashboard), found by a sync. */
  "email_changed",
  // Control Center actions (spec section 6.2). Each one carries the staff email and the reason.
  "trial_changed",
  "discount_applied",
  "discount_removed",
  "access_kept",
  "refunded",
  "paused",
  "resumed",
  "credit_added",
  "price_moved",
  /** Control Center made the business a pilot: no trial, no plan, no charge. A trial with no card was cancelled. */
  "pilot_started",
  /** Control Center ended the pilot. The 7-day trial starts on that day. */
  "pilot_ended",
  // Packages (AI Assistant, decided 2026-10-21), added and removed by the owner or an admin in Desk.
  "package_added",
  /** Removed: the item left Stripe, and the package stays on until `endsAt` in the data. */
  "package_removed",
] as const;
export type BillingEventKind = (typeof billingEventKinds)[number];

export const bannerKinds = ["trial_ending", "past_due", "read_only", "canceling", "paused"] as const;
export type BannerKind = (typeof bannerKinds)[number];

/** The parts of a discount that decide whether a charge is free. */
export type BillingDiscountInput = {
  percentOff: number | null;
  duration: "once" | "repeating" | "forever";
  endsAt: Date | string | null;
};

/** The fields of a subscription that decide access. Dates may come as Date or ISO strings. */
export type BillingAccessInput = {
  state: BillingState;
  trialEndsAt: Date | string | null;
  currentPeriodEnd: Date | string | null;
  graceEndsAt: Date | string | null;
  pauseAccess: PauseAccess;
  accessOverrideUntil: Date | string | null;
  hasCard: boolean;
  /** Optional: with a 100% discount the trial end charges nothing, so no card is needed yet. */
  discount?: BillingDiscountInput | null;
};

export type BillingAccess = {
  access: "full" | "read_only";
  banner: BannerKind | null;
  /** A short machine reason, for logs, tests and the 402 problem detail. */
  reason: string;
};

const DAY_MS = 86_400_000;

function time(value: Date | string | null): number | null {
  if (value === null) return null;
  const ms = typeof value === "string" ? Date.parse(value) : value.getTime();
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Does the owner (and an admin) get full Desk, or read-only? Staff, members and parents are never
 * limited: the customers of a business must not suffer when the business does not pay.
 *
 * `subscription` is null when the business has no row yet (a demo business, or one whose Stripe
 * subscription the create job has not made yet). That is never the business's fault, so it is full.
 */
export function billingAccess(
  subscription: BillingAccessInput | null,
  now: Date = new Date(),
): BillingAccess {
  if (!subscription) return { access: "full", banner: null, reason: "no_subscription" };
  const at = now.getTime();

  const override = time(subscription.accessOverrideUntil);
  const overridden = override !== null && override > at;

  switch (subscription.state) {
    case "trialing": {
      const end = time(subscription.trialEndsAt);
      const free = end !== null && chargeIsFree(subscription.discount ?? null, end);
      const ending = !subscription.hasCard && !free && end !== null && end - at <= TRIAL_BANNER_DAYS * DAY_MS;
      return { access: "full", banner: ending ? "trial_ending" : null, reason: "trialing" };
    }
    case "active":
      return { access: "full", banner: null, reason: "active" };
    case "canceling":
      return { access: "full", banner: "canceling", reason: "canceling" };
    case "paused":
      if (subscription.pauseAccess === "full" || overridden) {
        return { access: "full", banner: null, reason: overridden ? "override" : "paused_full" };
      }
      return { access: "read_only", banner: "paused", reason: "paused_read_only" };
    case "past_due": {
      if (overridden) return { access: "full", banner: "past_due", reason: "override" };
      const graceEnd = time(subscription.graceEndsAt);
      // No grace end means the row has not been through a sync yet: give the benefit of the doubt.
      if (graceEnd === null || at < graceEnd) return { access: "full", banner: "past_due", reason: "grace" };
      return { access: "read_only", banner: "read_only", reason: "grace_over" };
    }
    case "ended":
      if (overridden) return { access: "full", banner: "read_only", reason: "override" };
      return { access: "read_only", banner: "read_only", reason: "ended" };
  }
}

/**
 * True when a charge at `at` (ms) costs nothing because of a 100% discount. `once` covers the next
 * invoice; `repeating` covers charges before its end; `forever` covers all of them.
 */
export function chargeIsFree(discount: BillingDiscountInput | null, at: number): boolean {
  if (!discount || discount.percentOff !== 100) return false;
  if (discount.duration !== "repeating") return true;
  const end = time(discount.endsAt);
  return end === null || end > at;
}

/** The grace end for a first failed charge at `failedAt`. */
export function graceEndFor(failedAt: Date): Date {
  return new Date(failedAt.getTime() + GRACE_DAYS * DAY_MS);
}

/**
 * Maps the provider's subscription status onto our state. Stripe statuses:
 * trialing, active, past_due, unpaid, canceled, incomplete, incomplete_expired, paused.
 * After the last retry Stripe marks a subscription `unpaid` (dashboard setting, spike 2026-10-04);
 * for us that is still `past_due` with its grace long over.
 */
export function billingStateFromProvider(input: {
  status: string;
  cancelAtPeriodEnd: boolean;
  pauseCollection: boolean;
}): BillingState {
  switch (input.status) {
    case "trialing":
      return input.cancelAtPeriodEnd ? "canceling" : "trialing";
    case "active":
      if (input.pauseCollection) return "paused";
      return input.cancelAtPeriodEnd ? "canceling" : "active";
    case "past_due":
    case "unpaid":
    case "incomplete":
    case "paused":
      return "past_due";
    default:
      return "ended";
  }
}
